import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Sparkles, Pencil, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { maskCNPJ, validaCNPJ, maskTelefone, validaTelefone } from "../lib/validators";
import { DeleteButton } from "../components/DeleteButton";
import { etapaInfo } from "../lib/etapasFunil";

const ORIGENS = ["whatsapp", "instagram", "indicacao", "visita", "site"];
const ETAPAS = ["lead", "contato", "visita", "proposta", "analise", "contrato", "efetivado"];

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function Leads() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDiretor = user?.role === "diretor" || user?.role === "supervisao";

  const [leads, setLeads] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nome: "", empresa: "", cnpj: "", telefone: "", email: "",
    origem: "site", valor_estimado: 0, observacoes: "", vendedor_id: "",
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };

  const loadVendedores = async () => {
    try {
      const { data } = await api.get("/comercial/vendedores");
      setVendedores(data);
    } catch {
      setVendedores([]);
    }
  };

  useEffect(() => {
    load();
    loadVendedores();
  }, []);

  const submit = async () => {
    if (form.cnpj && !validaCNPJ(form.cnpj)) {
      toast.error("CNPJ inválido — verifique o número digitado"); return;
    }
    if (form.telefone && !validaTelefone(form.telefone)) {
      toast.error("Telefone inválido (DDD 11-99)"); return;
    }
    try {
      const payload = { ...form, valor_estimado: Number(form.valor_estimado) };
      if (!payload.vendedor_id) delete payload.vendedor_id;
      await api.post("/leads", payload);
      toast.success("Lead criado!");
      setOpen(false);
      setForm({ nome: "", empresa: "", cnpj: "", telefone: "", email: "", origem: "site", valor_estimado: 0, observacoes: "", vendedor_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar lead");
    }
  };

  const canEdit = (lead) => {
    if (!user) return false;
    if (user.role === "diretor" || user.role === "supervisao") return true;
    if (user.role === "vendedor") return !lead.vendedor_id || lead.vendedor_id === user.id;
    return false;
  };

  const abrirEdicao = (lead) => {
    setEditing(lead);
    setEditForm({
      nome: lead.nome || "",
      empresa: lead.empresa || "",
      cnpj: lead.cnpj || "",
      telefone: lead.telefone || "",
      email: lead.email || "",
      origem: lead.origem || "site",
      valor_estimado: lead.valor_estimado || 0,
      observacoes: lead.observacoes || "",
      vendedor_id: lead.vendedor_id || "",
      status_funil: lead.status_funil || "lead",
    });
    setEditOpen(true);
  };

  const salvarEdicao = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...editForm, valor_estimado: Number(editForm.valor_estimado) };
      // Vendedor não pode reatribuir, só diretor
      if (!isDiretor) delete payload.vendedor_id;
      await api.put(`/leads/${editing.id}`, payload);
      toast.success("Lead atualizado!");
      setEditOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const scoreAll = async () => {
    const pendentes = leads.filter((x) => x.score_ia == null);
    if (!pendentes.length) { toast.info("Todos os leads já possuem score"); return; }
    toast.info(`Calculando score de ${pendentes.length} lead(s)`);
    let falhas = 0;
    for (const lead of pendentes) {
      try {
        await api.post("/ai/score-lead", { lead_id: lead.id });
      } catch { falhas += 1; }
    }
    load();
    if (falhas) toast.error(`${falhas} lead(s) não foram pontuados. Tente novamente em alguns minutos`);
    else toast.success("Scores atualizados");
  };

  const nomeVendedor = (vid) => {
    if (!vid) return "—";
    return vendedores.find((v) => v.id === vid)?.nome || vid.slice(0, 8);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Captação e qualificação inicial · {leads.length} leads cadastrados
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="score-all-btn" variant="outline" onClick={scoreAll}>
            <Sparkles size={14} className="mr-2" /> Pontuar tudo (IA)
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-lead-btn" className="text-white" style={{ background: "#F97316" }}>
                <Plus size={14} className="mr-2" /> Novo lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Cadastrar lead</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nome do contato</Label>
                  <Input data-testid="lead-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Empresa</Label>
                  <Input data-testid="lead-empresa" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} />
                </div>
                <div>
                  <Label>CNPJ</Label>
                  <Input
                    data-testid="lead-cnpj"
                    value={form.cnpj}
                    onChange={(e) => setForm({ ...form, cnpj: maskCNPJ(e.target.value) })}
                    placeholder="00.000.000/0000-00"
                    className={form.cnpj && !validaCNPJ(form.cnpj) ? "border-red-500" : ""}
                  />
                  {form.cnpj && !validaCNPJ(form.cnpj) && (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="cnpj-error">CNPJ inválido — verifique o número digitado</p>
                  )}
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    data-testid="lead-telefone"
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: maskTelefone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    className={form.telefone && !validaTelefone(form.telefone) ? "border-red-500" : ""}
                  />
                  {form.telefone && !validaTelefone(form.telefone) && (
                    <p className="text-[11px] text-red-600 mt-1">Telefone inválido</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>E-mail</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label>Origem</Label>
                  <Select value={form.origem} onValueChange={(v) => setForm({ ...form, origem: v })}>
                    <SelectTrigger data-testid="lead-origem"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORIGENS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor estimado (R$)</Label>
                  <Input type="number" value={form.valor_estimado} onChange={(e) => setForm({ ...form, valor_estimado: e.target.value })} />
                </div>
                {isDiretor && (
                  <div className="col-span-2">
                    <Label>Atribuir a vendedor</Label>
                    <Select value={form.vendedor_id || "_none"} onValueChange={(v) => setForm({ ...form, vendedor_id: v === "_none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="(automático)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— (sem atribuição)</SelectItem>
                        {vendedores.map((v) => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <Label>Observações</Label>
                  <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                </div>
              </div>
              <Button data-testid="lead-submit" onClick={submit} className="w-full mt-2 text-white" style={{ background: "#1B3A6B" }}>
                Cadastrar
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contato</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>Estágio</TableHead>
              <TableHead>Vendedor</TableHead>
              <TableHead className="text-right">Valor estimado</TableHead>
              <TableHead className="text-center">Score IA</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-medium">{l.nome}</TableCell>
                <TableCell>{l.empresa}</TableCell>
                <TableCell><Badge variant="outline">{l.origem}</Badge></TableCell>
                <TableCell><Badge variant="outline" style={{ color: etapaInfo(l.status_funil).color, borderColor: etapaInfo(l.status_funil).color }} data-testid={`lead-etapa-${l.id}`}>{etapaInfo(l.status_funil).label}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{nomeVendedor(l.vendedor_id)}</TableCell>
                <TableCell className="text-right">{fmtMoney(l.valor_estimado)}</TableCell>
                <TableCell className="text-center font-bold" style={{ color: "#F97316" }} data-testid={`lead-score-${l.id}`} title={l.score_ia == null ? "Score em cálculo" : undefined}>{l.score_ia ?? "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/app/leads/${l.id}`)}
                      data-testid={`lead-detail-${l.id}`}
                      title="Ver detalhes"
                    >
                      <ExternalLink size={14} />
                    </Button>
                    {canEdit(l) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => abrirEdicao(l)}
                        data-testid={`lead-edit-${l.id}`}
                        title="Editar"
                      >
                        <Pencil size={14} />
                      </Button>
                    )}
                    <DeleteButton
                      url={`/leads/${l.id}`}
                      label={`Lead ${l.nome || l.empresa || l.id.slice(0, 8)}`}
                      onDeleted={load}
                      testId={`del-lead-${l.id}`}
                      iconOnly
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                  Nenhum lead cadastrado ainda.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Drawer lateral de edição */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="lead-edit-drawer">
          <SheetHeader>
            <SheetTitle>Editar lead — {editing?.nome}</SheetTitle>
          </SheetHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="col-span-2">
                <Label>Nome do contato</Label>
                <Input data-testid="edit-nome" value={editForm.nome} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Empresa</Label>
                <Input data-testid="edit-empresa" value={editForm.empresa} onChange={(e) => setEditForm({ ...editForm, empresa: e.target.value })} />
              </div>
              <div><Label>CNPJ</Label><Input value={editForm.cnpj} onChange={(e) => setEditForm({ ...editForm, cnpj: maskCNPJ(e.target.value) })} className={editForm.cnpj && !validaCNPJ(editForm.cnpj) ? "border-red-500" : ""} /></div>
              <div><Label>Telefone</Label><Input value={editForm.telefone} onChange={(e) => setEditForm({ ...editForm, telefone: maskTelefone(e.target.value) })} className={editForm.telefone && !validaTelefone(editForm.telefone) ? "border-red-500" : ""} /></div>
              <div className="col-span-2"><Label>E-mail</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div>
                <Label>Origem</Label>
                <Select value={editForm.origem} onValueChange={(v) => setEditForm({ ...editForm, origem: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ORIGENS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Etapa do funil</Label>
                <Select value={editForm.status_funil} onValueChange={(v) => setEditForm({ ...editForm, status_funil: v })}>
                  <SelectTrigger data-testid="edit-etapa"><SelectValue /></SelectTrigger>
                  <SelectContent>{ETAPAS.map((s) => <SelectItem key={s} value={s}>{etapaInfo(s).label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor estimado (R$)</Label>
                <Input type="number" value={editForm.valor_estimado} onChange={(e) => setEditForm({ ...editForm, valor_estimado: e.target.value })} />
              </div>
              {isDiretor && (
                <div>
                  <Label>Vendedor responsável</Label>
                  <Select value={editForm.vendedor_id || "_none"} onValueChange={(v) => setEditForm({ ...editForm, vendedor_id: v === "_none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-vendedor"><SelectValue placeholder="(nenhum)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— (nenhum)</SelectItem>
                      {vendedores.map((v) => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <Label>Observações</Label>
                <Textarea value={editForm.observacoes} onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })} />
              </div>
              <div className="col-span-2 flex gap-2 mt-4">
                <Button
                  data-testid="edit-save"
                  onClick={salvarEdicao}
                  disabled={saving}
                  className="flex-1 text-white"
                  style={{ background: "#1B3A6B" }}
                >
                  {saving ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Salvando…</>) : "Salvar alterações"}
                </Button>
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancelar</Button>
                <Button variant="ghost" onClick={() => navigate(`/app/leads/${editing.id}`)}>Ver histórico</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
